import React, { ReactNode, useCallback, useEffect } from 'react';
import { TreeViewDataItem, TreeView, DrawerPanelContent, DrawerPanelBody } from '@patternfly/react-core';
import { ExclamationCircleIcon, LockIcon } from '@patternfly/react-icons'
import { useDispatch, useSelector } from 'react-redux';

import './customization-page.css';
import { getIncludeDeprecatedModels, getIncludeNonLicensedModels, getIncludeUserHiddenModels, selectCustomizationTreeViewItems } from '../../store/selectors';
import { CustomizationObjectType, CustomizationTreeViewDataItem } from '../../store/model-customization-types';
import { useTranslation } from 'react-i18next';
import { convertTreeViewDataIntoItemHierarchy, showCustomizationTreeViewDataItem } from '../../store/model-customization-helpers';
import CustomizationFilters from './CustomizationFilters';

import iconBody from '../../img/body-outline.png';
import iconCombination from '../../img/combination-outline.png';
import iconFemalePelvis from '../../img/female-pelvis-outline.png';
import iconHead from '../../img/head-outline.png';
import iconTorso from '../../img/torso-outline.png';
import i18next from 'i18next';

interface CustomizationSideBarProps {
    treeViewData: TreeViewDataItem[],
    onTreeViewDataUpdate: (data: TreeViewDataItem[]) => void,
    onTreeViewItemSelect: (item: TreeViewDataItem) => void,
    activeTreeViewItems: TreeViewDataItem[],
}

/** Creates a getTreeViewItemName function with include filters (includeNonLicensedModelsState etc) already pre-set */
const makeGetTreeViewItemName = (includeNonLicensedModelsState: boolean,
    includeDeprecatedModelsState: boolean,
    includeUserHiddenModelsState: boolean) => {
    return (item: CustomizationTreeViewDataItem, noLicenseWarning: string) =>
        getTreeViewItemName(item,
            noLicenseWarning,
            includeNonLicensedModelsState,
            includeDeprecatedModelsState,
            includeUserHiddenModelsState);
}

const getTreeViewItemName = (item: CustomizationTreeViewDataItem,
    noLicenseWarning: string,
    includeNonLicensedModelsState: boolean,
    includeDeprecatedModelsState: boolean,
    includeUserHiddenModelsState: boolean): ReactNode => {
    const label = `${item.label}${item.isModified ? ' *' : ''}`;
    const labelNode = item.isAvailable ? label : (<span className="tree-view-no-license" title={noLicenseWarning}><span className="label">{label}</span><LockIcon /></span>);

    if (item.isValid) {
        return labelNode;
    } else {
        // we need to deduce whether this item would've been hidden by default if it was valid
        const isHiddenByDefault = (item.type === CustomizationObjectType.CustomizationBase || item.type === CustomizationObjectType.CustomizationOutput)
            && showCustomizationTreeViewDataItem(item,
                includeNonLicensedModelsState,
                includeDeprecatedModelsState,
                includeUserHiddenModelsState,
                true) === false;

        const title = isHiddenByDefault ? i18next.t('translation:customizationPage.validationError.hiddenByDefault') : undefined;

        return (<span className="tree-view-validation-error">
            <ExclamationCircleIcon />
            <span className={`label${isHiddenByDefault ? ' is-hidden-by-default' : ''}`} title={title}>{labelNode}</span>
        </span>)
    }
}

const CustomizationSideBar = (props: CustomizationSideBarProps) => {
    const { onTreeViewDataUpdate, treeViewData, onTreeViewItemSelect, activeTreeViewItems } = props;

    const dispatch = useDispatch();

    const { t } = useTranslation();
    const noLicenseWarning = t('customizationPage.noLicense.tooltip');


    const customizationHierarchy = useSelector(selectCustomizationTreeViewItems);
    const includeNonLicensedModelsState = useSelector(getIncludeNonLicensedModels);
    const includeDeprecatedModelsState = useSelector(getIncludeDeprecatedModels);
    const includeUserHiddenModelsState = useSelector(getIncludeUserHiddenModels);

    // generate tree view data items
    useEffect(() => {
        // pre-populate common body part groupings in correct order
        const headerItems: TreeViewDataItem[] = [
            { name: t('bodyPart.category.head'), id: 'Head', children: undefined, icon: <img src={iconHead} alt="head" width={30} /> },
            { name: t('bodyPart.category.torso'), id: 'Torso', children: undefined, icon: <img src={iconTorso} alt="torso" width={30} /> },
            { name: t('bodyPart.category.pelvis'), id: 'Pelvis', children: undefined, icon: <img src={iconFemalePelvis} alt="pelvis" width={28} /> },
            { name: t('bodyPart.category.combination'), id: 'Combination', children: undefined, icon: <img src={iconCombination} alt="combination" width={30} /> },
            { name: t('bodyPart.category.wholeBody'), id: 'WholeBody', children: undefined, icon: <img src={iconBody} alt="whole body" width={30} /> },
        ];


        const data = convertTreeViewDataIntoItemHierarchy(customizationHierarchy,
            makeGetTreeViewItemName(includeNonLicensedModelsState, includeDeprecatedModelsState, includeUserHiddenModelsState),
            noLicenseWarning,
            headerItems,
            includeNonLicensedModelsState,
            includeDeprecatedModelsState,
            includeUserHiddenModelsState);

        onTreeViewDataUpdate(data);

    }, [customizationHierarchy, noLicenseWarning, onTreeViewDataUpdate, t, includeNonLicensedModelsState, includeDeprecatedModelsState, includeUserHiddenModelsState, dispatch]);

    const handleSelect = useCallback((_, item: TreeViewDataItem) => {
        if (item.id === undefined) { throw new Error('TreeView item has no ID'); }
        onTreeViewItemSelect(item);
    }, [onTreeViewItemSelect]);

    if (treeViewData.length < 2) {
        // patternfly's TreeView will crash if we try to render a 0-sized data array, so just return an empty component
        // if we're still initializing data
        // also don't display anything if we only have one item (the "All structures" item) since that means we haven't loaded
        // in any models/customizations yet

        return null;
    }

    return (
        <DrawerPanelContent hasNoBorder defaultSize="300px">
            <DrawerPanelBody hasNoPadding>
                <div className="mv-compact-tabs">
                    <CustomizationFilters />
                    <TreeView
                        data={treeViewData}
                        onSelect={handleSelect}
                        // activeItems={[{ name: '', id: activeTreeViewId }]}
                        activeItems={activeTreeViewItems}
                        hasGuides
                        className="customization-tree-view"
                        useMemo
                    />
                </div>
            </DrawerPanelBody>
        </DrawerPanelContent>
    );
}

export default CustomizationSideBar;
